import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { useLang } from "@/i18n";
import { articles } from "@/blogData";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import { Reveal } from "@/components/Reveal";

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (!article) {
      navigate("/blog");
      return;
    }
    const c = article[lang];
    document.title = `${c.title} — Service Plus`;
    window.scrollTo(0, 0);
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "article-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: c.title,
      description: c.excerpt,
      datePublished: article.date,
      author: { "@type": "Organization", name: "Service Plus" },
      publisher: { "@type": "Organization", name: "Service Plus" },
    });
    document.head.appendChild(script);
    return () => document.getElementById("article-schema")?.remove();
  }, [article, lang, navigate]);

  if (!article) return null;
  const c = article[lang];

  return (
    <main>
      <Navbar />
      <section className="bg-[#0E1B2A] pb-16 pt-36">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Link data-testid="article-back-link" to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-[#FAF8F5]/60 transition-colors hover:text-[#D4AF37]">
            <ArrowLeft size={15} />
            {t.blog.back}
          </Link>
          <h1 data-testid="article-title" className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-[#FAF8F5] text-balance">
            {c.title}
          </h1>
          <p className="mt-5 flex flex-wrap items-center gap-5 text-xs uppercase tracking-widest text-[#FAF8F5]/50">
            <span className="flex items-center gap-1.5"><Calendar size={13} />{new Date(article.date).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span className="flex items-center gap-1.5"><Clock size={13} />{article.readTime} {t.blog.min}</span>
          </p>
        </div>
      </section>

      <div className="bg-[#FAF8F5]">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal className="-mt-0 pt-12">
            <div className="overflow-hidden rounded-2xl shadow-[0_30px_60px_-20px_rgba(14,27,42,0.25)]">
              <img
                src={article.image}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = article.fallback; }}
                alt={c.title}
                className="h-64 w-full object-cover sm:h-80"
              />
            </div>
          </Reveal>

          <article data-testid="article-body" className="py-14">
            <p className="font-serif text-xl italic leading-relaxed text-[#334155]">{c.excerpt}</p>
            {c.sections.map((s, i) => (
              <Reveal key={i} className="mt-10">
                <h2 className="font-serif text-2xl font-semibold text-[#0E1B2A]">{s.h}</h2>
                <p className="mt-4 text-base leading-relaxed text-[#475569]">{s.p}</p>
              </Reveal>
            ))}

            <Reveal className="mt-14 rounded-2xl bg-[#0E1B2A] p-8 text-center sm:p-10">
              <p className="font-serif text-2xl font-medium text-[#FAF8F5]">{t.blog.cta}</p>
              <Link
                data-testid="article-cta-btn"
                to="/#contact"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/");
                  setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 400);
                }}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#C87A57] px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#b06a48] hover:-translate-y-0.5"
              >
                {t.blog.ctaBtn}
              </Link>
            </Reveal>
          </article>
        </div>
      </div>
      <Footer />
      <ChatWidget />
    </main>
  );
}
